import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GlowButton from '@/components/base/GlowButton';
import ModalOverlay from '@/components/base/ModalOverlay';

interface SubmitModalProps {
  open: boolean;
  onClose: () => void;
}

const categories = ['IT & Tech', 'Finance', 'Games', 'Health & Wellness', 'Arts & Culture', 'Education', 'Business', 'Other'];

export default function SubmitModal({ open, onClose }: SubmitModalProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (!open) {
      setStatus('idle');
      setCharCount(0);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const desc = (data.get('description') as string) ?? '';
    if (desc.length > 500) return;

    const params = new URLSearchParams();
    data.forEach((val, key) => { params.append(key, val as string); });

    setStatus('submitting');
    try {
      const res = await fetch('https://readdy.ai/api/form/d7eqardfi84lst2k09bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <ModalOverlay open={open} onClose={onClose} zIndex={50}>
      {/* Modal — 毛玻璃风格 */}
      <div className="w-full max-w-lg bg-white/75 backdrop-blur-2xl border border-white/90 rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0088CC]/12 backdrop-blur-sm border border-[#0088CC]/20">
              <i className="ri-add-circle-line text-[#0088CC] text-base"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">Submit Your Community</h2>
              <p className="text-xs text-slate-400">Get listed and reach thousands of users</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white/60 backdrop-blur-sm rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100/80 backdrop-blur-sm border border-emerald-200/60 mb-4">
                <i className="ri-check-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">Submission Received!</h3>
              <p className="text-sm text-slate-400 mb-6">We'll review your community and get back to you within 1–2 business days.</p>
              <GlowButton onClick={onClose} className="px-6 py-2.5 text-sm font-semibold rounded-xl">
                Got it
              </GlowButton>
            </div>
          ) : (
            <form
              data-readdy-form
              id="submit-community-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              {/* Community Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Community Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="community_name"
                  required
                  placeholder="e.g. CryptoTalk Global"
                  className="w-full px-3.5 py-2.5 text-sm bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl text-slate-700 placeholder-slate-300 focus:outline-none focus:border-[#0088CC]/40 focus:bg-white/70 transition-colors"
                />
              </div>

              {/* Telegram Link */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Telegram Link <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-2 px-3.5 py-2.5 bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl focus-within:border-[#0088CC]/40 focus-within:bg-white/70 transition-colors">
                  <i className="ri-telegram-fill text-[#0088CC] text-base shrink-0"></i>
                  <input
                    type="url"
                    name="telegram_link"
                    required
                    placeholder="https://t.me/yourcommunity"
                    className="flex-1 text-sm bg-transparent text-slate-700 placeholder-slate-300 focus:outline-none"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Category <span className="text-red-400">*</span>
                </label>
                <select
                  name="category"
                  required
                  defaultValue=""
                  className="w-full px-3.5 py-2.5 text-sm bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl text-slate-700 focus:outline-none focus:border-[#0088CC]/40 focus:bg-white/70 transition-colors cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select a category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Member Count */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Approximate Member Count
                </label>
                <select
                  name="member_count"
                  defaultValue=""
                  className="w-full px-3.5 py-2.5 text-sm bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl text-slate-700 focus:outline-none focus:border-[#0088CC]/40 focus:bg-white/70 transition-colors cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select range</option>
                  <option value="under_500">Under 500</option>
                  <option value="500_5000">500 – 5,000</option>
                  <option value="5000_50000">5,000 – 50,000</option>
                  <option value="over_50000">Over 50,000</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Short Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  required
                  rows={3}
                  maxLength={500}
                  placeholder="Tell us what your community is about..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-3.5 py-2.5 text-sm bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl text-slate-700 placeholder-slate-300 focus:outline-none focus:border-[#0088CC]/40 focus:bg-white/70 transition-colors resize-none"
                />
                <p className={`text-right text-[11px] mt-1 ${charCount > 480 ? 'text-red-400' : 'text-slate-300'}`}>
                  {charCount}/500
                </p>
              </div>

              {/* Ad Placement Contact */}
              <div className="flex items-start gap-3 px-4 py-3 bg-amber-50/60 backdrop-blur-sm border border-amber-200/50 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100/70 backdrop-blur-sm shrink-0 mt-0.5">
                  <i className="ri-advertisement-line text-amber-500 text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-800 leading-tight">Want to advertise here?</p>
                  <p className="text-[11px] text-amber-600 mt-0.5">Contact us to purchase premium ad placements on this site</p>
                  <a
                    href="/contact"
                    className="text-[11px] text-[#0088CC] hover:underline cursor-pointer mt-1 inline-block"
                    onClick={(e) => { e.preventDefault(); onClose(); navigate('/contact'); }}
                  >
                    View all contact options →
                  </a>
                </div>
                <GlowButton
                  as="a"
                  href="https://t.me/liketgli"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs px-3 py-1.5 rounded-lg shrink-0 whitespace-nowrap"
                >
                  <i className="ri-telegram-fill text-sm"></i>
                  Contact Us
                </GlowButton>
              </div>

              {status === 'error' && (
                <p className="text-xs text-red-400 text-center">Something went wrong. Please try again.</p>
              )}

              <GlowButton
                type="submit"
                disabled={status === 'submitting' || charCount > 500}
                className="w-full py-3 text-sm font-bold rounded-xl mt-1"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Submitting...
                  </>
                ) : 'Submit Community'}
              </GlowButton>
            </form>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
